import { useFeatureValue } from '@growthbook/growthbook-react';
import { addDays, isAfter } from 'date-fns';
import { useEffect, useMemo } from 'react';

import {
  selectNewFeatureAlertState,
  useAppDispatch,
  useAppSelector,
  userPreferencesActions,
} from '@/store';
import { NewFeatureAlert } from '@/types';
import { enableFeatureReset } from '@/utils/development/development.feature';

const DEFAULT_TTL = 30;
const FLAG_KEY = 'new-feature-alert-time-to-live-in-days';

interface FeatureAlertOptions {
  isEnabled?: boolean;
  ttl?: number;
}

export const useNewFeatureAlert = (
  id: NewFeatureAlert,
  { isEnabled = true, ttl = DEFAULT_TTL }: FeatureAlertOptions = {},
) => {
  const dispatch = useAppDispatch();
  const rawTtlDays = useFeatureValue(FLAG_KEY, ttl);
  const ttlDays = validatedTtlDays(rawTtlDays);

  const alert = useAppSelector(state => selectNewFeatureAlertState(state, id));
  const firstSeenAt = alert?.firstSeenAt;
  const isDismissed = alert?.isDismissed;

  const isExpired = useMemo(() => {
    if (!firstSeenAt) return false;

    const expiryDate = addDays(new Date(firstSeenAt), ttlDays);
    return isAfter(new Date(), expiryDate);
  }, [firstSeenAt, ttlDays]);

  const isVisible = isEnabled && !isDismissed && !isExpired;

  useEffect(() => {
    enableFeatureReset(dispatch);

    if (!isVisible) return;
    dispatch(userPreferencesActions.markNewFeatureAlertSeen(id));
  }, [dispatch, isVisible, id]);

  return {
    isVisible,
    dismiss: () => {
      if (isVisible) {
        dispatch(userPreferencesActions.dismissNewFeatureAlert(id));
      }
    },
  };
};

const validatedTtlDays = (ttlDays: number) =>
  typeof ttlDays === 'number' && ttlDays > 0 ? ttlDays : DEFAULT_TTL;
